import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Plus, Inbox, Send, Barcode, Search, Download } from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Correspondence = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [correspondence, setCorrespondence] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchCorrespondence();
  }, []);

  const fetchCorrespondence = async () => {
    try {
      const response = await api.get('/correspondence');
      setCorrespondence(response.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل المراسلات' : 'Failed to load correspondence');
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي المراسلات' : 'Total',
      value: correspondence.length,
      icon: Mail,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'الوارد' : 'Incoming',
      value: correspondence.filter(c => c.correspondence_type === 'incoming').length,
      icon: Inbox,
      color: 'from-green-500 to-green-600'
    },
    {
      title: i18n.language === 'ar' ? 'الصادر' : 'Outgoing',
      value: correspondence.filter(c => c.correspondence_type === 'outgoing').length,
      icon: Send,
      color: 'from-orange-500 to-orange-600'
    },
  ];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">{i18n.language === 'ar' ? 'الصادر والوارد' : 'Correspondence'}</h1>
              <p className="text-text-secondary text-lg">{i18n.language === 'ar' ? 'إدارة المراسلات بنظام الباركود' : 'Manage correspondence with barcode system'}</p>
            </div>
            <Button className="btn-primary gap-2">
              <Plus className="w-5 h-5" />
              {i18n.language === 'ar' ? 'مراسلة جديدة' : 'New Correspondence'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-text-secondary mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="all">
            <TabsList>
              <TabsTrigger value="all">{i18n.language === 'ar' ? 'الكل' : 'All'}</TabsTrigger>
              <TabsTrigger value="incoming">{i18n.language === 'ar' ? 'الوارد' : 'Incoming'}</TabsTrigger>
              <TabsTrigger value="outgoing">{i18n.language === 'ar' ? 'الصادر' : 'Outgoing'}</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                      <Input
                        placeholder={i18n.language === 'ar' ? 'بحث بالباركود أو الموضوع...' : 'Search by barcode or subject...'}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {correspondence.length === 0 ? (
                    <div className="text-center py-20">
                      <Mail className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                      <p className="text-text-secondary">{i18n.language === 'ar' ? 'لا توجد مراسلات' : 'No correspondence yet'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {correspondence.map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex items-center gap-4 flex-1">
                            <div className="flex flex-col items-center">
                              <Barcode className="w-8 h-8 mb-1" style={{ color: '#9D7E4F' }} />
                              <span className="text-xs font-mono">{item.barcode}</span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-1">
                                <p className="font-semibold">{item.subject}</p>
                                <Badge variant={item.correspondence_type === 'incoming' ? 'default' : 'secondary'}>
                                  {i18n.language === 'ar' 
                                    ? (item.correspondence_type === 'incoming' ? 'وارد' : 'صادر')
                                    : item.correspondence_type
                                  }
                                </Badge>
                              </div>
                              <div className="text-sm text-text-secondary">
                                <span>{i18n.language === 'ar' ? 'من:' : 'From:'} {item.from_entity}</span>
                                <span className="mx-2">→</span>
                                <span>{i18n.language === 'ar' ? 'إلى:' : 'To:'} {item.to_entity}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-text-secondary">{format(new Date(item.date), 'dd/MM/yyyy')}</span>
                            <Button size="sm" variant="outline"><Download className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Correspondence;